import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  role: string;
  schoolId: string | null;
  schoolName: string | null;
  createdAt: string;
  lastSignInAt: string | null;
}

interface School {
  id: string;
  name: string;
  isDefault?: boolean;
  /** SaaS licence expiry; null = perpetual */
  expiresAt?: string | null;
  contactEmail?: string | null;
  notes?: string | null;
}

/** Licence state for a school row, shown as a chip in the schools table. */
function licenceChip(expiresAt?: string | null) {
  if (!expiresAt) return { label: '永久', color: 'default' as const };
  const days = Math.ceil((Date.parse(expiresAt) - Date.now()) / 86_400_000);
  if (days < 0) return { label: `已過期 ${Math.abs(days)} 天`, color: 'error' as const };
  if (days <= 30) return { label: `${days} 天後到期`, color: 'warning' as const };
  return { label: `至 ${new Date(expiresAt).toLocaleDateString('zh-HK')}`, color: 'success' as const };
}

interface UsageGroup {
  key: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  failures: number;
}

interface UsageReport {
  days: number;
  total: Omit<UsageGroup, 'key'>;
  byModel: UsageGroup[];
  byRole: UsageGroup[];
  bySchool: UsageGroup[];
  byDay: UsageGroup[];
  bySubmission: UsageGroup[];
  recent: Array<{
    ts: string;
    provider: string;
    model: string;
    role?: string;
    submissionId?: string;
    inputTokens: number;
    outputTokens: number;
    costUsd: number | null;
    durationMs: number;
    success: boolean;
  }>;
}

async function adminFetch(path: string, init?: RequestInit) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  const res = await fetch(`/api/admin${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      ...init?.headers,
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

const fmtCost = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(4)}`);
const fmtTokens = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v));

export function AdminPage() {
  const { role } = useAuth();
  const [tab, setTab] = useState(0);

  if (role === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (role !== 'admin') {
    return <Alert severity="error">此頁面僅限管理員存取</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        管理員
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="測試帳戶" />
        <Tab label="用量與成本" />
        <Tab label="模型設定" />
      </Tabs>
      {tab === 0 ? <UsersTab /> : tab === 1 ? <UsageTab /> : <SettingsTab />}
    </Box>
  );
}

// ── Users tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', role: 'teacher', schoolId: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ users }, { schools }] = await Promise.all([adminFetch('/users'), adminFetch('/schools')]);
      setUsers(users);
      setSchools(schools);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const [schoolDialog, setSchoolDialog] = useState<{ open: boolean; editing?: School }>({ open: false });
  const [schoolForm, setSchoolForm] = useState({ name: '', expiresAt: '', contactEmail: '', notes: '' });

  const openSchoolDialog = (editing?: School) => {
    setSchoolForm({
      name: editing?.name ?? '',
      expiresAt: editing?.expiresAt ? editing.expiresAt.slice(0, 10) : '',
      contactEmail: editing?.contactEmail ?? '',
      notes: editing?.notes ?? '',
    });
    setSchoolDialog({ open: true, editing });
  };

  const handleSaveSchool = async () => {
    setSaving(true);
    setError(null);
    try {
      // date input gives YYYY-MM-DD; treat it as end of that day in local time
      const payload = {
        name: schoolForm.name.trim(),
        expiresAt: schoolForm.expiresAt ? new Date(`${schoolForm.expiresAt}T23:59:59`).toISOString() : null,
        contactEmail: schoolForm.contactEmail.trim() || null,
        notes: schoolForm.notes.trim() || null,
      };
      if (schoolDialog.editing) {
        await adminFetch(`/schools/${schoolDialog.editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setNotice(`已更新 ${payload.name}`);
      } else {
        await adminFetch('/schools', { method: 'POST', body: JSON.stringify(payload) });
        setNotice(`已新增學校 ${payload.name}`);
      }
      setSchoolDialog({ open: false });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleChangeSchool = async (u: AdminUser, schoolId: string) => {
    try {
      await adminFetch(`/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ schoolId: schoolId || null }) });
      setNotice(`已更新 ${u.email} 的學校`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminFetch('/users', { method: 'POST', body: JSON.stringify(form) });
      setNotice(`已建立帳戶 ${form.email}`);
      setCreateOpen(false);
      setForm({ email: '', password: '', fullName: '', role: 'teacher', schoolId: '' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (u: AdminUser) => {
    const password = window.prompt(`為 ${u.email} 設定新密碼（至少 6 個字元）：`);
    if (!password) return;
    try {
      await adminFetch(`/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ password }) });
      setNotice(`已重設 ${u.email} 的密碼`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleDelete = async (u: AdminUser) => {
    if (!window.confirm(`確定要刪除帳戶 ${u.email}？此操作無法復原。`)) return;
    try {
      await adminFetch(`/users/${u.id}`, { method: 'DELETE' });
      setNotice(`已刪除 ${u.email}`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          測試版不開放自行註冊；在此為 beta 客戶開通帳戶。
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => openSchoolDialog()}>
            新增學校
          </Button>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            新增帳戶
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>電郵</TableCell>
                <TableCell>名稱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>學校</TableCell>
                <TableCell>建立時間</TableCell>
                <TableCell>最後登入</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.fullName || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={u.role} color={u.role === 'admin' ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      variant="standard"
                      value={u.schoolId ?? ''}
                      onChange={(e) => handleChangeSchool(u, e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value="">
                        <em>未指定</em>
                      </MenuItem>
                      {schools.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>{new Date(u.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleString() : '從未登入'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="重設密碼">
                      <IconButton size="small" onClick={() => handleResetPassword(u)}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="刪除帳戶">
                      <IconButton size="small" onClick={() => handleDelete(u)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Schools — the SaaS licensing unit */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              學校與使用期限
            </Typography>
            <Button size="small" variant="outlined" onClick={() => openSchoolDialog()}>
              新增學校
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            使用期限屆滿後，該校老師仍可登入及查看已有資料，但無法新增作業或批改，直至續期。
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>學校</TableCell>
                  <TableCell>使用期限</TableCell>
                  <TableCell>聯絡電郵</TableCell>
                  <TableCell>帳戶數</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.map((sc) => {
                  const chip = licenceChip(sc.expiresAt);
                  return (
                    <TableRow key={sc.id}>
                      <TableCell>{sc.name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === 'default' ? 'outlined' : 'filled'} />
                      </TableCell>
                      <TableCell>{sc.contactEmail || '—'}</TableCell>
                      <TableCell>{users.filter((u) => u.schoolId === sc.id).length}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openSchoolDialog(sc)}>
                          編輯
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {schools.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        尚未建立學校。
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* School dialog — same shape as 新增帳戶 */}
      <Dialog open={schoolDialog.open} onClose={() => setSchoolDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{schoolDialog.editing ? '編輯學校' : '新增學校'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="學校名稱"
              value={schoolForm.name}
              onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="使用期限"
              type="date"
              value={schoolForm.expiresAt}
              onChange={(e) => setSchoolForm({ ...schoolForm, expiresAt: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="留空表示無期限。屆滿後老師仍可登入，但不能批改。"
              fullWidth
            />
            <TextField
              label="聯絡電郵（可選）"
              type="email"
              value={schoolForm.contactEmail}
              onChange={(e) => setSchoolForm({ ...schoolForm, contactEmail: e.target.value })}
              fullWidth
            />
            <TextField
              label="備註（可選）"
              value={schoolForm.notes}
              onChange={(e) => setSchoolForm({ ...schoolForm, notes: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolDialog({ open: false })}>取消</Button>
          <Button variant="contained" onClick={handleSaveSchool} disabled={saving || !schoolForm.name.trim()}>
            {saving ? <CircularProgress size={20} /> : schoolDialog.editing ? '儲存' : '建立'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>新增測試帳戶</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="電郵"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="密碼"
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              helperText="至少 6 個字元；請透過安全渠道交給客戶"
              fullWidth
              required
            />
            <TextField
              label="名稱"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="學校"
              value={form.schoolId}
              onChange={(e) => setForm({ ...form, schoolId: e.target.value })}
              helperText="沒有想要的學校？先用「新增學校」建立"
              fullWidth
            >
              <MenuItem value="">
                <em>未指定</em>
              </MenuItem>
              {schools.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="角色"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              fullWidth
            >
              <MenuItem value="teacher">teacher</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.email || form.password.length < 6}>
            {saving ? <CircularProgress size={20} /> : '建立'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Usage tab ────────────────────────────────────────────────────────────────

function UsageTab() {
  const [report, setReport] = useState<UsageReport | null>(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await adminFetch(`/usage?days=${days}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField select size="small" label="時間範圍" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <MenuItem value={1}>今天（24 小時）</MenuItem>
          <MenuItem value={7}>最近 7 天</MenuItem>
          <MenuItem value={30}>最近 30 天</MenuItem>
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={load}>
          重新整理
        </Button>
      </Stack>

      {loading || !report ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <SummaryCard title="總成本 (USD)" value={fmtCost(report.total.costUsd)} />
            <SummaryCard title="模型呼叫次數" value={String(report.total.calls)} />
            <SummaryCard title="輸入 tokens" value={fmtTokens(report.total.inputTokens)} />
            <SummaryCard title="輸出 tokens" value={fmtTokens(report.total.outputTokens)} />
            <SummaryCard title="失敗次數" value={String(report.total.failures)} />
          </Stack>

          <GroupTable title="按學校" rows={report.bySchool} keyLabel="學校" />
          <GroupTable title="按模型" rows={report.byModel} keyLabel="模型" />
          <GroupTable title="按階段 (agent role)" rows={report.byRole} keyLabel="階段" />
          <GroupTable title="按日" rows={report.byDay} keyLabel="日期" />
          <GroupTable title="成本最高的 submissions" rows={report.bySubmission} keyLabel="Submission" />

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                最近呼叫
              </Typography>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>時間</TableCell>
                      <TableCell>模型</TableCell>
                      <TableCell>階段</TableCell>
                      <TableCell align="right">in</TableCell>
                      <TableCell align="right">out</TableCell>
                      <TableCell align="right">成本</TableCell>
                      <TableCell align="right">耗時</TableCell>
                      <TableCell>狀態</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.recent.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(e.ts).toLocaleTimeString()}</TableCell>
                        <TableCell>{`${e.provider}/${e.model}`}</TableCell>
                        <TableCell>{e.role ?? '—'}</TableCell>
                        <TableCell align="right">{fmtTokens(e.inputTokens)}</TableCell>
                        <TableCell align="right">{fmtTokens(e.outputTokens)}</TableCell>
                        <TableCell align="right">{fmtCost(e.costUsd)}</TableCell>
                        <TableCell align="right">{`${(e.durationMs / 1000).toFixed(1)}s`}</TableCell>
                        <TableCell>
                          <Chip size="small" label={e.success ? 'OK' : '失敗'} color={e.success ? 'success' : 'error'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}

// ── Settings tab (models & API keys; mirrors TMC AI settings) ────────────────

interface CatalogueModel {
  provider: string;
  model: string;
  label?: string;
  vision?: boolean;
}

interface SettingsResponse {
  agents: Record<string, {
    provider: string; model: string; source: 'admin' | 'env';
    fallbackProvider: string | null; fallbackModel: string | null;
  }>;
  keys: Record<string, { set: boolean; source: 'admin' | 'env' | null; hint: string | null }>;
  providers: string[];
  catalogue: CatalogueModel[];
  visionRoles: string[];
  updatedAt: string | null;
}

/** Dropdown value encoding — one control instead of provider + free-text model. */
const modelKey = (provider: string, model: string) => `${provider}|${model}`;
const parseModelKey = (key: string) => {
  const [provider, ...rest] = key.split('|');
  return { provider: provider ?? '', model: rest.join('|') };
};
const modelLabel = (m: CatalogueModel) => `${m.label ?? m.model} · ${m.provider}`;

/**
 * Stages the live grading pipeline does not call yet. Kept visible (greyed) so
 * the planned capability isn't forgotten, but not editable — a setting that
 * silently does nothing is worse than no setting.
 */
const FUTURE_ROLES: Record<string, string> = {
  verifier: '未來功能：批改後由第二個模型覆核分數與理據，用以提升準確度。目前批改流程未啟用此階段，設定不會生效。',
};

const ROLE_LABELS: Record<string, string> = {
  questionIdentifier: '題目偵測 (questionIdentifier)',
  transcription: '手寫辨識 (transcription)',
  essayGrader: '作文批改 (essayGrader)',
  grader: '題目批改 (grader)',
  verifier: '覆核 (verifier)',
  rubricLoader: '評分準則建議 (rubric suggestion)',
};

function SettingsTab() {
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [agentEdits, setAgentEdits] = useState<Record<string, { primary: string; fallback: string }>>({});
  const [catalogueEdits, setCatalogueEdits] = useState<CatalogueModel[]>([]);
  const [newModel, setNewModel] = useState<CatalogueModel>({ provider: 'openrouter', model: '', label: '', vision: false });
  const [keyEdits, setKeyEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s: SettingsResponse = await adminFetch('/settings');
      setSettings(s);
      setAgentEdits(
        Object.fromEntries(Object.entries(s.agents).map(([role, cfg]) => [role, {
          primary: modelKey(cfg.provider, cfg.model),
          fallback: cfg.fallbackProvider && cfg.fallbackModel ? modelKey(cfg.fallbackProvider, cfg.fallbackModel) : '',
        }])),
      );
      setCatalogueEdits(s.catalogue);
      setKeyEdits({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const agents: Record<string, { provider?: string; model?: string; fallbackProvider?: string; fallbackModel?: string }> = {};
      for (const [role, edit] of Object.entries(agentEdits)) {
        const current = settings.agents[role];
        if (!current) continue;
        const p = parseModelKey(edit.primary);
        const f = edit.fallback ? parseModelKey(edit.fallback) : { provider: '', model: '' };
        const primaryChanged = p.provider !== current.provider || p.model !== current.model;
        const fallbackChanged =
          f.provider !== (current.fallbackProvider ?? '') || f.model !== (current.fallbackModel ?? '');
        if (primaryChanged || fallbackChanged) {
          agents[role] = {
            provider: p.provider, model: p.model,
            fallbackProvider: f.provider, fallbackModel: f.model,
          };
        }
      }
      const keys: Record<string, string> = {};
      for (const [name, value] of Object.entries(keyEdits)) {
        if (value.trim()) keys[name] = value.trim();
      }
      const catalogueChanged = JSON.stringify(catalogueEdits) !== JSON.stringify(settings.catalogue);
      await adminFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify({ agents, keys, ...(catalogueChanged ? { catalogue: catalogueEdits } : {}) }),
      });
      setNotice('設定已儲存，下一次批改立即生效');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            各階段模型
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            此處設定優先於環境變數；儲存後下一次批改生效。只能從下方「可用模型」清單中選擇，避免打錯字。
            <strong>備用模型</strong>：主模型失敗或回覆空白時自動改用，老師端不受影響。
          </Typography>
          <Box
            sx={{
              display: 'grid',
              // fixed columns so every row's dropdowns line up regardless of
              // how many chips the label carries
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 260px) minmax(240px, 1fr) minmax(240px, 1fr)' },
              columnGap: 2,
              rowGap: 2,
              alignItems: 'center',
            }}
          >
            {/* column headers, so the two dropdown columns are labelled once */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              主模型
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              備用模型（主模型失敗時自動使用）
            </Typography>

            {Object.entries(agentEdits).map(([role, edit]) => {
              const needsVision = settings.visionRoles.includes(role);
              const options = catalogueEdits.filter((m) => !needsVision || m.vision);
              const futureNote = FUTURE_ROLES[role];
              const disabled = Boolean(futureNote);
              // Tooltip must anchor to an element that has a layout box — wrapping the
              // whole row in `display: contents` gave it none, so it rendered at the
              // page corner. Each cell is wrapped individually instead.
              const withNote = (node: React.ReactNode) =>
                futureNote ? (
                  <Tooltip title={futureNote} placement="top" arrow>
                    <Box sx={{ cursor: 'help', minWidth: 0 }}>{node}</Box>
                  </Tooltip>
                ) : (
                  node
                );
              const labelCell = (
                  <Box sx={{ minWidth: 0, opacity: disabled ? 0.5 : 1 }}>
                    <Typography variant="body2">{ROLE_LABELS[role] ?? role}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      {!disabled && settings.agents[role]?.source === 'admin' && (
                        <Chip label="admin" size="small" color="secondary" />
                      )}
                      {needsVision && <Chip label="需辨識圖片" size="small" variant="outlined" />}
                      {disabled && <Chip label="未啟用" size="small" variant="outlined" />}
                    </Stack>
                  </Box>
              );
              const primaryField = (
                  <TextField
                    select
                    size="small"
                    disabled={disabled}
                    value={edit.primary}
                    onChange={(e) => setAgentEdits({ ...agentEdits, [role]: { ...edit, primary: e.target.value } })}
                    fullWidth
                  >
                    {options.map((m) => (
                      <MenuItem key={modelKey(m.provider, m.model)} value={modelKey(m.provider, m.model)}>
                        {modelLabel(m)}
                      </MenuItem>
                    ))}
                    {/* keep a stale/env value selectable so nothing silently changes */}
                    {!options.some((m) => modelKey(m.provider, m.model) === edit.primary) && (
                      <MenuItem value={edit.primary}>{edit.primary.replace('|', ' · ')}（不在清單）</MenuItem>
                    )}
                  </TextField>
              );
              const fallbackField = (
                  <TextField
                    select
                    size="small"
                    disabled={disabled}
                    value={edit.fallback}
                    onChange={(e) => setAgentEdits({ ...agentEdits, [role]: { ...edit, fallback: e.target.value } })}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>不設定</em>
                    </MenuItem>
                    {options
                      .filter((m) => modelKey(m.provider, m.model) !== edit.primary)
                      .map((m) => (
                        <MenuItem key={modelKey(m.provider, m.model)} value={modelKey(m.provider, m.model)}>
                          {modelLabel(m)}
                        </MenuItem>
                      ))}
                  </TextField>
              );
              return (
                <Fragment key={role}>
                  {withNote(labelCell)}
                  {withNote(primaryField)}
                  {withNote(fallbackField)}
                </Fragment>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            可用模型清單
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            上方下拉選單的選項來源。新增前請先確認模型在該供應商可用（OpenRouter 用 <code>供應商/模型</code> 格式，如
            <code> google/gemini-3.7-flash</code>）。「可辨識圖片」的模型才能用於題目偵測與手寫辨識。
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>名稱</TableCell>
                  <TableCell>供應商</TableCell>
                  <TableCell>模型 ID</TableCell>
                  <TableCell align="center">可辨識圖片</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogueEdits.map((m) => {
                  const inUse = Object.values(agentEdits).some(
                    (e) => e.primary === modelKey(m.provider, m.model) || e.fallback === modelKey(m.provider, m.model),
                  );
                  return (
                    <TableRow key={modelKey(m.provider, m.model)}>
                      <TableCell>{m.label ?? '—'}</TableCell>
                      <TableCell>{m.provider}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{m.model}</TableCell>
                      <TableCell align="center">{m.vision ? '✓' : ''}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={inUse ? '使用中，無法移除' : '從清單移除'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={inUse}
                              onClick={() =>
                                setCatalogueEdits(catalogueEdits.filter((x) => !(x.provider === m.provider && x.model === m.model)))
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="供應商"
              value={newModel.provider}
              onChange={(e) => setNewModel({ ...newModel, provider: e.target.value })}
              sx={{ width: 150 }}
            >
              {settings.providers.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="模型 ID"
              placeholder="google/gemini-3.7-flash"
              value={newModel.model}
              onChange={(e) => setNewModel({ ...newModel, model: e.target.value })}
              sx={{ minWidth: 280 }}
            />
            <TextField
              size="small"
              label="顯示名稱（可選）"
              value={newModel.label}
              onChange={(e) => setNewModel({ ...newModel, label: e.target.value })}
              sx={{ width: 200 }}
            />
            <TextField
              select
              size="small"
              label="可辨識圖片"
              value={newModel.vision ? 'yes' : 'no'}
              onChange={(e) => setNewModel({ ...newModel, vision: e.target.value === 'yes' })}
              sx={{ width: 140 }}
            >
              <MenuItem value="yes">是</MenuItem>
              <MenuItem value="no">否</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              disabled={!newModel.model.trim()}
              onClick={() => {
                const model = newModel.model.trim();
                if (catalogueEdits.some((m) => m.provider === newModel.provider && m.model === model)) return;
                setCatalogueEdits([...catalogueEdits, { ...newModel, model, label: newModel.label?.trim() || undefined }]);
                setNewModel({ provider: 'openrouter', model: '', label: '', vision: false });
              }}
            >
              加入清單
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            API 金鑰
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            金鑰只寫入不回讀；留空即保持現值。清除管理員設定的金鑰請輸入一個空格後儲存。
          </Typography>
          <Stack spacing={2}>
            {Object.entries(settings.keys).map(([name, info]) => (
              <Stack key={name} direction="row" spacing={2} alignItems="center">
                <Typography sx={{ minWidth: 260, fontFamily: 'monospace', fontSize: 13 }}>{name}</Typography>
                <Chip
                  size="small"
                  label={info.set ? `已設定 (${info.source}) ${info.hint}` : '未設定'}
                  color={info.set ? 'success' : 'default'}
                />
                <TextField
                  size="small"
                  type="password"
                  placeholder={info.set ? '••••••••（輸入以覆蓋）' : '輸入金鑰'}
                  value={keyEdits[name] ?? ''}
                  onChange={(e) => setKeyEdits({ ...keyEdits, [name]: e.target.value })}
                  sx={{ flex: 1, maxWidth: 360 }}
                />
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : '儲存設定'}
        </Button>
        {settings.updatedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            上次更新：{new Date(settings.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function GroupTable({ title, rows, keyLabel }: { title: string; rows: UsageGroup[]; keyLabel: string }) {
  if (rows.length === 0) return null;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{keyLabel}</TableCell>
                <TableCell align="right">呼叫</TableCell>
                <TableCell align="right">輸入 tokens</TableCell>
                <TableCell align="right">輸出 tokens</TableCell>
                <TableCell align="right">成本 (USD)</TableCell>
                <TableCell align="right">失敗</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.key}</TableCell>
                  <TableCell align="right">{r.calls}</TableCell>
                  <TableCell align="right">{fmtTokens(r.inputTokens)}</TableCell>
                  <TableCell align="right">{fmtTokens(r.outputTokens)}</TableCell>
                  <TableCell align="right">{fmtCost(r.costUsd)}</TableCell>
                  <TableCell align="right">{r.failures}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
